/**
 * SoleadLab 2.0 - Alarm Sound Service
 *
 * Web Audio API-based sound notifications for monitoring alarms.
 * Distinct tones for warning (double beep) vs critical (alarm siren).
 * Respects browser autoplay policy (requires user interaction to enable).
 */

import type { AlarmSeverity } from '@/types/monitoring';

// ============================================
// State
// ============================================

let audioContext: AudioContext | null = null;
let enabled = true;
let unlocked = false;

// ============================================
// Audio Context Management
// ============================================

function getAudioContext(): AudioContext | null {
  if (!audioContext) {
    try {
      audioContext = new AudioContext();
    } catch {
      console.warn('Web Audio API not available');
      return null;
    }
  }
  return audioContext;
}

/**
 * Must be called from a user interaction (click/tap) to unlock audio.
 * Call this when the user enables alarm sounds.
 */
export async function unlockAudio(): Promise<void> {
  const ctx = getAudioContext();
  if (!ctx) return;

  if (ctx.state === 'suspended') {
    await ctx.resume();
  }
  unlocked = true;
}

// ============================================
// Sound Generation
// ============================================

/**
 * Play a warning sound: two short beeps at 880Hz.
 */
function playWarningSound(): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  const now = ctx.currentTime;

  // First beep
  playBeep(ctx, 880, now, 0.15, 0.3);
  // Second beep (after 200ms gap)
  playBeep(ctx, 880, now + 0.35, 0.15, 0.3);
}

/**
 * Play a critical alarm sound: descending two-tone siren.
 */
function playCriticalSound(): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  const now = ctx.currentTime;

  // High tone
  playBeep(ctx, 1200, now, 0.2, 0.5);
  // Low tone
  playBeep(ctx, 800, now + 0.25, 0.2, 0.5);
  // High tone again
  playBeep(ctx, 1200, now + 0.5, 0.2, 0.5);
}

/**
 * Play a single beep tone.
 */
function playBeep(
  ctx: AudioContext,
  frequency: number,
  startTime: number,
  duration: number,
  volume: number
): void {
  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();

  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(frequency, startTime);

  // Envelope: quick attack, hold, quick release
  gainNode.gain.setValueAtTime(0, startTime);
  gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.02); // 20ms attack
  gainNode.gain.setValueAtTime(volume, startTime + duration - 0.02);
  gainNode.gain.linearRampToValueAtTime(0, startTime + duration); // 20ms release

  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);

  oscillator.start(startTime);
  oscillator.stop(startTime + duration);
}

// ============================================
// Public API
// ============================================

/**
 * Play the alarm sound for a given severity level.
 * Does nothing if sounds are disabled or audio hasn't been unlocked.
 */
export function playAlarmSound(severity: AlarmSeverity): void {
  if (!enabled || !unlocked) return;

  switch (severity) {
    case 'warning':
      playWarningSound();
      break;
    case 'critical':
      playCriticalSound();
      break;
  }
}

/**
 * Enable or disable alarm sounds.
 */
export function setAlarmSoundEnabled(value: boolean): void {
  enabled = value;
}

/**
 * Check if alarm sounds are currently enabled.
 */
export function isAlarmSoundEnabled(): boolean {
  return enabled;
}

/**
 * Check if audio has been unlocked by user interaction.
 */
export function isAudioUnlocked(): boolean {
  return unlocked;
}
